"use client";

import React from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Text,
  Box,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { whatsappAgentService } from "../../whatsapp-services/agentService";
import "flag-icons/css/flag-icons.css";

interface WhatsAppLanguageSelectProps {
  agentId?: string;
  initialLanguage?: string;
  value?: string;
  onChange?: (language: string) => void;
  onLanguageUpdate?: (language: string) => void;
}

const languageOptions = [
  { code: "english", name: "English", flag: "us" },
  { code: "german", name: "German", flag: "de" },
  { code: "spanish", name: "Spanish", flag: "es" },
  { code: "hindi", name: "Hindi", flag: "in" },
  { code: "japanese", name: "Japanese", flag: "jp" },
  { code: "portuguese", name: "Portuguese", flag: "pt" },
  { code: "french", name: "French", flag: "fr" },
  { code: "chinese", name: "Chinese", flag: "cn" },
  { code: "russian", name: "Russian", flag: "ru" },
  { code: "italian", name: "Italian", flag: "it" },
  { code: "korean", name: "Korean", flag: "kr" },
  { code: "dutch", name: "Dutch", flag: "nl" },
  { code: "polish", name: "Polish", flag: "pl" },
  { code: "turkish", name: "Turkish", flag: "tr" },
  { code: "vietnamese", name: "Vietnamese", flag: "vn" },
  { code: "multilingual", name: "Multilingual", flag: "un" },
];

export const WhatsAppLanguageSelect: React.FC<WhatsAppLanguageSelectProps> =
  React.memo(
    ({ agentId, initialLanguage, value, onChange, onLanguageUpdate }) => {
      const isControlled = value !== undefined && onChange !== undefined;
      const [selectedLanguage, setSelectedLanguage] = React.useState(
        isControlled ? value : initialLanguage || "english"
      );
      const toast = useToast();

      React.useEffect(() => {
        if (isControlled) {
          setSelectedLanguage(value);
        }
      }, [value, isControlled]);

      const handleLanguageChange = React.useCallback(
        async (languageCode: string) => {
          if (isControlled) {
            onChange(languageCode);
            return;
          }

          if (!agentId) return;

          try {
            await whatsappAgentService.update(agentId, {
              language: languageCode,
            });
            setSelectedLanguage(languageCode);
            onLanguageUpdate?.(languageCode);

            toast({
              title: "Language updated successfully",
              status: "success",
              duration: 2000,
            });
          } catch (error: any) {
            toast({
              title: "Error updating language",
              description: error?.message || "An unexpected error occurred",
              status: "error",
              duration: 3000,
            });
          }
        },
        [agentId, onLanguageUpdate, toast, isControlled, onChange]
      );

      const selectedLang =
        languageOptions.find((lang) => lang.code === selectedLanguage) ||
        languageOptions[0];

      return (
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            rightIcon={<ChevronDownIcon />}
            colorScheme="green"
            borderColor="green.400"
            color="gray.800"
            _hover={{ bg: "green.50", borderColor: "green.500" }}
            _active={{ bg: "green.100", borderColor: "green.600" }}
            _focusVisible={{
              borderColor: "green.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-green-500)",
            }}
          >
            <HStack spacing={2}>
              <Box
                as="span"
                className={`fi fi-${selectedLang.flag}`}
                width="1.3em"
                height="1.3em"
              />
              <Text color="gray.800">{selectedLang.name}</Text>
            </HStack>
          </MenuButton>

          <MenuList
            maxH="300px"
            overflowY="auto"
            borderColor="green.200"
            _focusWithin={{
              borderColor: "green.300",
              boxShadow: "0 0 0 1px var(--chakra-colors-green-300)",
            }}
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "var(--chakra-colors-green-400) transparent",

              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "var(--chakra-colors-green-400)",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "var(--chakra-colors-green-500)",
              },
            }}
          >
            {languageOptions.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                _hover={{ bg: "green.50" }}
                _focus={{ bg: "green.50" }}
                _active={{ bg: "green.100" }}
              >
                <HStack spacing={2}>
                  <Box
                    as="span"
                    className={`fi fi-${lang.flag}`}
                    width="1.3em"
                    height="1.3em"
                  />
                  <Text>{lang.name}</Text>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      );
    }
  );

export default WhatsAppLanguageSelect;
